import React, { useState, useRef } from 'react';
import { string, number, func, bool } from 'prop-types';
import {
  Typography,
  useBreakpoint,
  IconButton,
  Button, ButtonGroup,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Tooltip
} from '@one-thd/sui-atomic-components';
import { Edit, TrashCan, WarningFilled } from '@one-thd/sui-icons';

import { Row, Col } from '@thd-olt-component-react/core-ui';
import { useConfigService } from '@thd-nucleus/experience-context';
import { useCabinetVisualizerStore } from '../../../../store';
import {
  calculateProductShifts,
  getConstrainedWallSize
} from '../utils';
import {
  tryGetQueryParam
} from '../../../../utils/queryParams';
import { CabinetInfoSection } from './CabinetInfoSection';

export const WallMeasurementInfoSection = ({
  wallName,
  width,
  height,
  handleEditClick,
  showDimensions,
  hasValidDesign,
  totalWidth,
  totalHeight,
  totalDepth
}) => {
  const hasWallMeasurements = Number(width) && Number(width) > 0 && Number(height) && Number(height) > 0;
  const isOutOfBound = Number(totalWidth) > Number(width) || Number(totalHeight) > Number(height);
  const [isDeleteMeasurementsModalOpen, setIsDeleteMeasurementsModalOpen] = useState(false); // NEW STATE
  const host = useConfigService('global-custom-commerce:host');
  const matches = useBreakpoint('sm');
  const isDesktop = matches.greater;
  const { cabinetVisualizer, updateDesign } = useCabinetVisualizerStore();
  const designId = cabinetVisualizer.selectedDesignId || cabinetVisualizer.styleDesignIds[0];
  const standardWallWidth = 104;
  const standardWallHeight = 84;
  const internalWallSize = useRef(getConstrainedWallSize({ Columns: standardWallWidth, Rows: standardWallHeight }));

  const getWidthHeightLabel = (label) => {
    return isDesktop ? label : label.charAt(0);
  };

  const shiftProductsOnWallResize = () => {
    const updatedProducts = calculateProductShifts(
      cabinetVisualizer,
      designId,
      standardWallWidth,
      standardWallHeight,
      internalWallSize
    );
    return updatedProducts;
  };

  // Measurement delete logic
  const handleDeleteMeasurementsClick = () => {
    setIsDeleteMeasurementsModalOpen(true);
  };

  const handleDeleteMeasurementsModalClose = () => {
    setIsDeleteMeasurementsModalOpen(false);
  };

  const handleDeleteMeasurementsModalConfirm = async () => {
    try {
      const currentDesignId = tryGetQueryParam('designid') || designId;
      // const updatedProducts = shiftProductsOnWallResize();
      await updateDesign(host, currentDesignId, {
        DesignName: wallName,
        Matrix: { Rows: 0, Columns: 0 },
        // Products: updatedProducts
      });
      setIsDeleteMeasurementsModalOpen(false);

    } catch (err) {
      console.error('Error saving measurements:', err);
    }
  };

  const EditDeleteMeasurementIcons = (label) => {
    return (
      <>
        <IconButton
          className="sui-align-self-start"
          icon={Edit}
          iconSize="small"
          onClick={handleEditClick}
          aria-label="Edit"
        />
        <IconButton
          className="sui-align-self-start"
          icon={TrashCan}
          iconSize="small"
          onClick={handleDeleteMeasurementsClick}
          aria-label="Delete Measurements"
        />
      </>
    );
  };

  const WallMeasurements = () => {
    return (
      width > 0 && height > 0
        ? (
          <>
            <Typography variant="body-xs" weight="regular">
              {getWidthHeightLabel('Width')}: <b>{`${width} in `}</b>
            </Typography>
            <Typography variant="body-xs" weight="regular">
              {getWidthHeightLabel('Height')}: <b>{`${height} in`}</b>
            </Typography>
            <EditDeleteMeasurementIcons />
          </>
        )
        : (
          <button
            type="button"
            onClick={handleEditClick}
            aria-label="Set Wall Measurements"
            className="sui-underline"
          >
            <Typography variant="body-xs" weight="regular">Set Wall Measurements</Typography>
          </button>
        )
    );
  };

  return (
    <>
      <div className="sui-flex sui-w-full sui-flex-wrap sui-pb-3">
        <div className="sui-flex sui-gap-x-2 sui-flex-grow sui-pb-3">
          { hasWallMeasurements && isOutOfBound ? (
            <Tooltip placement="bottom" title="Your design exceeds your set wall measurements">
              <WarningFilled size="small" style={{ color: '#EBB820' }} />
            </Tooltip>
          ) : null}
          {isDesktop ? (<Typography variant="body-xs" weight="bold">Available Wall Space: </Typography>) : null}
          <WallMeasurements />
        </div>
        {!showDimensions && hasValidDesign && (
          <div className=" sui-pb-3">
            <CabinetInfoSection isDesktop={isDesktop} width={totalWidth} height={totalHeight} depth={totalDepth} />
          </div>
        )}
      </div>
      {/* Measurement Delete Confirmation Modal */}
      <Modal open={isDeleteMeasurementsModalOpen} onClose={handleDeleteMeasurementsModalClose}>
        <ModalHeader onClose={handleDeleteMeasurementsModalClose}>Delete Measurements</ModalHeader>
        <ModalBody>
          <Row className="sui-pl-0 sui-pr-0">
            <Col className="sui-pl-0 sui-pr-0" fallback="12">
              <Typography>
                {/* eslint-disable-next-line max-len */}
                {'Are you sure you want to delete your wall measurements? This will keep your wall name and layout intact, but will remove the wall measurements.'}
              </Typography>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter position="end">
          <ButtonGroup>
            <Button
              variant="secondary"
              onClick={handleDeleteMeasurementsModalClose}
              style={isDesktop ? { order: 0 } : { order: 1 }}
            >
              Cancel
            </Button>
            <Button variant="primary" onClick={handleDeleteMeasurementsModalConfirm}>Delete Measurements</Button>
          </ButtonGroup>
        </ModalFooter>
      </Modal>
    </>
  );
};

WallMeasurementInfoSection.propTypes = {
  wallName: string.isRequired,
  width: number.isRequired,
  height: number.isRequired,
  handleEditClick: func.isRequired,
  showDimensions: bool.isRequired,
  hasValidDesign: bool.isRequired,
  totalWidth: number,
  totalHeight: number,
  totalDepth: number
};

WallMeasurementInfoSection.defaultProps = {
  totalWidth: 0,
  totalHeight: 0,
  totalDepth: 0
};
